import { initFirebase } from '../firebase';

/**
 * Generate a unique trace ID
 * @returns {string} Unique trace ID
 */
function generateTraceId() {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Get or create session ID
 * @returns {string} Session ID
 */
function getSessionId() {
  let sessionId = sessionStorage.getItem('fluente_session_id');
  if (!sessionId) {
    sessionId = `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('fluente_session_id', sessionId);
  }
  return sessionId;
}

/**
 * Create tracing info object
 * @param {Object} extraInfo - Additional tracing fields
 * @returns {Object} Tracing info
 */
function createTracingInfo(extraInfo = {}) {
  const { auth } = initFirebase();
  const user = auth.currentUser;
  
  return {
    trace_id: generateTraceId(),
    session_id: getSessionId(),
    source: 'web',
    user_id: user?.uid || null,
    timestamp: new Date().toISOString(),
    user_agent: navigator.userAgent,
    ...extraInfo
  };
}

/**
 * Base API client configuration
 */
class ApiClient {
  constructor(baseUrl) {
    this.baseUrl = baseUrl || import.meta.env.VITE_CALENDAR_API_URL || 'http://localhost:8080';
    this.apiKey = import.meta.env.VITE_FLUENTE_API_KEY || '';
  }

  /**
   * Get authorization headers with Firebase ID token
   * @param {string} clientId - Client/customer ID for X-Client-ID header
   * @param {string} traceId - Optional trace ID for request tracing
   * @returns {Promise<Object>} Headers object
   */
  async getHeaders(clientId = null, traceId = null) {
    const { auth } = initFirebase();
    const user = auth.currentUser;
    const headers = {
      'Content-Type': 'application/json',
    };

    // Add API key authentication
    if (this.apiKey) {
      headers['X-API-Key'] = this.apiKey;
    }

    if (user) {
      try {
        const token = await user.getIdToken();
        headers['Authorization'] = `Bearer ${token}`;
      } catch (error) {
        console.error('Error getting ID token:', error);
      }
    }

    if (clientId) {
      headers['X-Client-ID'] = clientId;
    }
    
    // Add tracing headers
    if (traceId) {
      headers['X-Trace-Id'] = traceId;
    }
    headers['X-Session-Id'] = getSessionId();
    headers['X-Source'] = 'web';

    return headers;
  }

  /**
   * Make GET request
   * @param {string} endpoint - API endpoint
   * @param {Object} options - Request options
   * @returns {Promise<Object>} Response data
   */
  async get(endpoint, { clientId = null, params = {} } = {}) {
    const traceId = generateTraceId();
    const headers = await this.getHeaders(clientId, traceId);
    const url = new URL(`${this.baseUrl}${endpoint}`);
    
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null) {
        url.searchParams.append(key, params[key]);
      }
    });

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Request failed' }));
      throw new Error(error.message || `HTTP error ${response.status}`);
    }

    return response.json();
  }

  /**
   * Make POST request
   * @param {string} endpoint - API endpoint
   * @param {Object} data - Request body data
   * @param {Object} options - Request options
   * @returns {Promise<Object>} Response data
   */
  async post(endpoint, data, { clientId = null } = {}) {
    const tracingInfo = createTracingInfo();
    const headers = await this.getHeaders(clientId, tracingInfo.trace_id);
    
    // Add tracing_info to request body
    const requestData = {
      ...data,
      tracing_info: tracingInfo
    };
    
    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      method: 'POST',
      headers,
      body: JSON.stringify(requestData),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Request failed' }));
      const errorMessage = error.error || error.message || `HTTP error ${response.status}`;
      console.error('API Error:', errorMessage, error);
      throw new Error(errorMessage);
    }

    return response.json();
  }

  /**
   * Make DELETE request
   * @param {string} endpoint - API endpoint
   * @param {Object} options - Request options
   * @returns {Promise<Object>} Response data
   */
  async delete(endpoint, { clientId = null } = {}) {
    const traceId = generateTraceId();
    const headers = await this.getHeaders(clientId, traceId);
    
    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      method: 'DELETE',
      headers,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Request failed' }));
      const errorMessage = error.error || error.message || `HTTP error ${response.status}`;
      console.error('API Error:', errorMessage, error);
      throw new Error(errorMessage);
    }

    return response.json();
  }

  /**
   * Make PUT request
   * @param {string} endpoint - API endpoint
   * @param {Object} data - Request body data
   * @param {Object} options - Request options
   * @returns {Promise<Object>} Response data
   */
  async put(endpoint, data, { clientId = null } = {}) {
    const headers = await this.getHeaders(clientId);
    
    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Request failed' }));
      const errorMessage = error.error || error.message || `HTTP error ${response.status}`;
      console.error('API Error:', errorMessage, error);
      throw new Error(errorMessage);
    }

    return response.json();
  }
}

export const apiClient = new ApiClient();
export default ApiClient;
